import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Shop } from '../../../models/vendor.model';
import { FEATURE_FLAGS } from '../../constants/feature-flags';

@Component({
  selector: 'app-shop-card',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './shop-card.component.html',
  styleUrls: ['./shop-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShopCardComponent {
  @Input({ required: true }) shop!: Shop;
  @Output() quickView = new EventEmitter<Shop>();

  readonly featureFlags = FEATURE_FLAGS;

  get bannerImage(): string {
    return this.shop.cover_image || this.shop.shop_image || 'assets/images/no-image.jpg';
  }

  get logoImage(): string {
    return this.shop.shop_image || 'assets/images/no-image.jpg';
  }

  get primaryCategory(): string | null {
    return this.categoryNames[0] ?? null;
  }

  get categoryNames(): string[] {
    if (this.shop.categories?.length) {
      return this.shop.categories.map(c => c.name).filter(Boolean);
    }
    if (this.shop.category_name) {
      return String(this.shop.category_name).split(',').map(name => name.trim()).filter(Boolean);
    }
    return [];
  }

  get shortAddress(): string | null {
    const location = this.shop.location;
    if (location?.city) {
      return location.state ? `${location.city}, ${location.state}` : location.city;
    }
    if (this.shop.address) {
      return String(this.shop.address).split(',').slice(0, 2).join(',').trim();
    }
    return null;
  }

  get isOpen(): boolean {
    return !!this.shop.is_accepting_orders;
  }

  /** No is_popular field exists on the backend - gated behind FEATURE_FLAGS.popularBadge. */
  get isPopular(): boolean {
    return (this.shop.rating ?? 0) >= 4.5 && (this.shop.review_count ?? 0) >= 20;
  }

  get deliveryTimeLabel(): string {
    const min = this.shop.delivery_time_min;
    const max = this.shop.delivery_time_max;
    if (min && max && min !== max) return `${min}-${max} min`;
    if (min || max) return `${min || max} min`;
    // No estimate from the shop - show nothing rather than an invented time.
    return '';
  }

  onQuickView(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.quickView.emit(this.shop);
  }
}
